import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { formatCost, formatUnixDateTime } from '@/shared/lib/datetime';
import { useRouteId } from '@/shared/lib/useRouteId';
import { DataTable, type DataColumn } from '@/shared/ui/DataTable';
import { DetailBox, DetailLayout } from '@/shared/ui/DetailLayout';
import { InfoList } from '@/shared/ui/InfoList';
import { fetchCartDetail, type CartInfo, type CartItem, type CartUser } from './api';

const ROLE_NAMES: Record<string, string> = { customer_individual: 'Клиент', service: 'Сервис' };

const rub = (value: number | string | undefined) => `${formatCost(value)} ₽`;

const ITEM_COLUMNS: DataColumn<CartItem>[] = [
  { header: 'Брэнд', render: (i) => i.brand_name },
  { header: 'Артикул', render: (i) => i.article_name },
  { header: 'Описание', render: (i) => i.description },
  { header: 'Количество', render: (i) => i.quantity },
  { header: 'Сумма для клиента', render: (i) => rub(i.customer_price) },
  { header: 'Сумма от поставщика', render: (i) => rub(i.distributor_price) },
];

const userItems = (user: CartUser): [string, string | number | undefined][] => {
  const role = user.user_role ?? '';
  const nameLabel = role === 'customer_individual' ? 'ФИО' : role === 'service' ? 'Название' : null;
  return [
    ['Роль', ROLE_NAMES[role]],
    ['ID пользователя', user.user_id],
    ['Дата регистрации', formatUnixDateTime(user.register_date)],
    ...(nameLabel ? [[nameLabel, user.user] as [string, string | undefined]] : []),
    ['Email', user.email],
    ['Телефон', user.phone ? `+${user.phone}` : ''],
    ['Баланс', user.balance ? rub(user.balance) : ''],
    ['Бонусный баланс 2', user.bonus_balance ? rub(user.bonus_balance) : ''],
  ];
};

const cartItems = (cart: CartInfo): [string, string | number | undefined][] => [
  ['Создание корзины', formatUnixDateTime(cart.create_time)],
  ['Обновление корзины', formatUnixDateTime(cart.update_time)],
  ['Всего заказов', cart.total_orders],
];

export const PartsCartPage = () => {
  const id = useRouteId();
  const { data, isLoading, error } = useQuery({ queryKey: ['parts-cart', id], queryFn: () => fetchCartDetail(id) });

  return (
    <DetailLayout title={`Корзина: ID ${id}`} isLoading={isLoading} error={error}>
      {data && (
        <>
          <div className="row">
            <div className="col-md-5">
              <DetailBox>
                <InfoList items={userItems(data.user ?? {})} />
              </DetailBox>
            </div>
            <div className="col-md-4">
              <DetailBox>
                <InfoList items={cartItems(data.cart ?? {})} />
              </DetailBox>
            </div>
          </div>
          <DataTable columns={ITEM_COLUMNS} rows={data.items ?? []} />
          <div className="clearfix">
            <Link to="/parts/carts" className="btn btn-primary pull-right">
              Назад
            </Link>
          </div>
        </>
      )}
    </DetailLayout>
  );
};
